import axios from 'axios';
import { UserRole } from '../types/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const apiClient = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export interface RolePermissionConfig {
    role: UserRole;
    pages: string[];
    canOperate: boolean;
}

/**
 * 獲取所有角色權限配置
 */
export const getAllRolePermissions = async (): Promise<Record<string, RolePermissionConfig>> => {
    const response = await apiClient.get<{
        success: boolean;
        data: { permissions: Record<string, RolePermissionConfig> };
    }>('/api/roles/permissions');

    if (response.data.success) {
        return response.data.data.permissions;
    }

    throw new Error('Failed to get role permissions');
};

/**
 * 更新特定角色的權限配置
 */
export const updateRolePermissions = async (
    role: UserRole,
    pages: string[],
    canOperate: boolean
): Promise<RolePermissionConfig> => {
    const response = await apiClient.put<{
        success: boolean;
        data: { config: RolePermissionConfig };
        message?: string;
    }>(`/api/roles/${role}/permissions`, { pages, canOperate });

    if (response.data.success) {
        return response.data.data.config;
    }

    throw new Error(response.data.message || 'Failed to update role permissions');
};

/**
 * 重置角色權限為默認值
 */
export const resetRolePermissions = async (role: UserRole): Promise<RolePermissionConfig> => {
    const response = await apiClient.post<{
        success: boolean;
        data: { config: RolePermissionConfig };
        message?: string;
    }>(`/api/roles/${role}/reset`);

    if (response.data.success) {
        return response.data.data.config;
    }

    throw new Error(response.data.message || 'Failed to reset role permissions');
};

export default {
    getAllRolePermissions,
    updateRolePermissions,
    resetRolePermissions,
};
